import { useState, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { useDropzone, type FileRejection } from 'react-dropzone';
import { motion, AnimatePresence } from 'framer-motion';
import { artworkService, BatchUploadResult, ArtworkUploadProgress } from '../../services';
import { LoadingSpinner, useConfirmDialog } from '../ui';

const UploadContainer = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
`;

const DropzoneContainer = styled.div<{ $isDragActive: boolean; $hasFiles: boolean }>`
  border: 2px dashed ${({ theme, $isDragActive, $hasFiles }) =>
    $isDragActive ? theme.colors.primary :
    $hasFiles ? theme.colors.success :
    theme.colors.gray.medium};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing['3xl']};
  text-align: center;
  background-color: ${({ theme, $isDragActive }) =>
    $isDragActive ? `${theme.colors.primary}10` : theme.colors.white};
  transition: all ${({ theme }) => theme.transitions.normal};
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background-color: ${({ theme }) => `${theme.colors.primary}05`};
  }
`;

const DropzoneIcon = styled.div<{ $isDragActive: boolean }>`
  font-size: 48px;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  transition: transform ${({ theme }) => theme.transitions.normal};
  transform: ${({ $isDragActive }) => $isDragActive ? 'scale(1.1)' : 'scale(1)'};
`;

const DropzoneTitle = styled.h3`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.text};
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
`;

const DropzoneText = styled.p`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  color: ${({ theme }) => theme.colors.gray.dark};
  line-height: 1.6;
`;

const BrowseButton = styled.button`
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  border: none;
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.xl};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.normal};

  &:hover {
    background-color: ${({ theme }) => theme.colors.secondary};
    transform: translateY(-2px);
  }
`;

const FilePreviewContainer = styled.div`
  margin-top: ${({ theme }) => theme.spacing.xl};
`;

const FilePreviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.spacing.lg};
  margin-top: ${({ theme }) => theme.spacing.lg};
`;

const FilePreviewCard = styled(motion.div)`
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  overflow: hidden;
  position: relative;
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 150px;
  object-fit: cover;
  background-color: ${({ theme }) => theme.colors.gray.light};
`;

const FileInfo = styled.div`
  padding: ${({ theme }) => theme.spacing.md};
`;

const FileName = styled.h4`
  margin: 0 0 ${({ theme }) => theme.spacing.xs};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text};
  word-break: break-word;
`;

const FileSize = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.gray.dark};
`;

const RemoveButton = styled.button`
  position: absolute;
  top: ${({ theme }) => theme.spacing.sm};
  right: ${({ theme }) => theme.spacing.sm};
  width: 24px;
  height: 24px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  border: none;
  background-color: rgba(220, 53, 69, 0.9);
  color: ${({ theme }) => theme.colors.white};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    background-color: rgba(220, 53, 69, 1);
    transform: scale(1.1);
  }
`;

const ProgressOverlay = styled.div<{ $progress: number }>`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 4px;
  background-color: ${({ theme }) => theme.colors.gray.light};

  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: ${({ $progress }) => $progress}%;
    background-color: ${({ theme }) => theme.colors.primary};
    transition: width 0.3s ease;
  }
`;

const StatusIndicator = styled.div<{ $status: ArtworkUploadProgress['status'] }>`
  position: absolute;
  top: ${({ theme }) => theme.spacing.sm};
  left: ${({ theme }) => theme.spacing.sm};
  width: 20px;
  height: 20px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  color: ${({ theme }) => theme.colors.white};
  
  ${({ $status, theme }) => {
    switch ($status) {
      case 'complete':
        return `background-color: ${theme.colors.success};`;
      case 'error':
        return `background-color: ${theme.colors.error};`;
      case 'uploading':
      case 'processing':
        return `background-color: ${theme.colors.primary};`;
      default:
        return `background-color: ${theme.colors.gray.medium};`;
    }
  }}
`;

const UploadActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${({ theme }) => theme.spacing.xl};
  gap: ${({ theme }) => theme.spacing.md};
  flex-wrap: wrap;
`;

const ActionButton = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.xl};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.normal};

  ${({ $variant = 'primary', theme }) => {
    switch ($variant) {
      case 'danger':
        return `
          background-color: ${theme.colors.error};
          color: ${theme.colors.white};
          border: none;
          &:hover:not(:disabled) {
            background-color: #c53030;
          }
        `;
      case 'secondary':
        return `
          background-color: transparent;
          color: ${theme.colors.primary};
          border: 1px solid ${theme.colors.primary};
          &:hover:not(:disabled) {
            background-color: ${theme.colors.gray.light};
          }
        `;
      default:
        return `
          background-color: ${theme.colors.primary};
          color: ${theme.colors.white};
          border: none;
          &:hover:not(:disabled) {
            background-color: ${theme.colors.secondary};
          }
        `;
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const UploadSummary = styled.div`
  background-color: ${({ theme }) => theme.colors.gray.light};
  padding: ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  margin-top: ${({ theme }) => theme.spacing.lg};
`;

const SummaryTitle = styled.h4`
  margin: 0 0 ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.text};
`;

const SummaryStats = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xl};
  flex-wrap: wrap;
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatValue = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.primary};
`;

const StatLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.gray.dark};
`;

export interface ImageUploadProps {
  maxFiles?: number;
  maxSize?: number; // in bytes
  acceptedFormats?: string[];
  onUploadStart?: () => void;
  onUploadComplete?: (results: BatchUploadResult) => void;
  onUploadError?: (error: string) => void;
  studentId?: number;
  tagIds?: number[];
  className?: string;
}

export function ImageUpload({
  maxFiles = 50,
  maxSize = 10 * 1024 * 1024, // 10MB
  acceptedFormats = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif', 'image/dng', 'image/tiff'],
  onUploadStart,
  onUploadComplete,
  onUploadError,
  studentId,
  tagIds = [],
  className,
}: ImageUploadProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [uploadProgress, setUploadProgress] = useState<Map<number, ArtworkUploadProgress>>(new Map());
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState<BatchUploadResult | null>(null);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { confirm: confirmClear, dialog: clearDialog } = useConfirmDialog({
    title: 'Clear Files',
    message: 'Are you sure you want to remove all selected files?',
    confirmText: 'Clear All',
    variant: 'warning',
  });

  const onDrop = useCallback((acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
    if (rejectedFiles.length > 0) {
      const errors = rejectedFiles.map(({ file, errors }) =>
        `${file.name}: ${errors.map((e) => e.message).join(', ')}`
      );
      onUploadError?.(errors.join('\n'));
      return;
    }

    if (files.length + acceptedFiles.length > maxFiles) {
      onUploadError?.(
        `Cannot add ${acceptedFiles.length} files. Maximum ${maxFiles} files allowed.`
      );
      return;
    }

    // Validate files
    const validFiles: File[] = [];
    const invalidFiles: string[] = [];

    acceptedFiles.forEach(file => {
      const validation = artworkService.validateImageFile(file);
      if (validation.valid) {
        validFiles.push(file);
      } else {
        invalidFiles.push(`${file.name}: ${validation.error}`);
      }
    });

    if (invalidFiles.length > 0) {
      onUploadError?.(invalidFiles.join('\n'));
    }

    if (validFiles.length > 0) {
      setFiles(prev => [...prev, ...validFiles]);
      
      // Generate previews
      validFiles.forEach(file => {
        const reader = new FileReader();
        reader.onload = () => {
          setPreviews(prev => [...prev, reader.result as string]);
        };
        reader.readAsDataURL(file);
      });
    }
  }, [files.length, maxFiles, onUploadError]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptedFormats.reduce((acc, format) => {
      acc[format] = [];
      return acc;
    }, {} as Record<string, string[]>),
    maxSize,
    multiple: true,
  });

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    setPreviews(prev => prev.filter((_, i) => i !== index));
    setUploadProgress(prev => {
      const newMap = new Map(prev);
      newMap.delete(index);
      return newMap;
    });
  };

  const clearAllFiles = () => {
    confirmClear(() => {
      setFiles([]);
      setPreviews([]);
      setUploadProgress(new Map());
      setUploadResults(null);
    });
  };

  const handleUpload = async () => {
    if (files.length === 0) return;

    setIsUploading(true);
    setUploadResults(null);
    onUploadStart?.();

    try {
      const results = await artworkService.batchUploadArtworks(
        files,
        { student_id: studentId, tag_ids: tagIds },
        {
          onProgress: (fileIndex, progress) => {
            setUploadProgress(prev => {
              const newMap = new Map(prev);
              newMap.set(fileIndex, {
                fileIndex,
                filename: files[fileIndex].name,
                progress,
                status: progress === 100 ? 'processing' : 'uploading',
              });
              return newMap;
            });
          },
          onFileComplete: (fileIndex, artwork) => {
            setUploadProgress(prev => {
              const newMap = new Map(prev);
              newMap.set(fileIndex, {
                fileIndex,
                filename: files[fileIndex].name,
                progress: 100,
                status: 'complete',
                artwork,
              });
              return newMap;
            });
          },
          onFileError: (fileIndex, error, filename) => {
            setUploadProgress(prev => {
              const newMap = new Map(prev);
              newMap.set(fileIndex, {
                fileIndex,
                filename,
                progress: 0,
                status: 'error',
                error,
              });
              return newMap;
            });
          },
        }
      );

      setUploadResults(results);
      onUploadComplete?.(results);

    } catch (error: any) {
      onUploadError?.(error?.message || 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusIcon = (status: ArtworkUploadProgress['status']) => {
    switch (status) {
      case 'complete':
        return '✓';
      case 'error':
        return '✗';
      case 'uploading':
      case 'processing':
        return '↑';
      default:
        return '○';
    }
  };

  return (
    <UploadContainer className={className}>
      {files.length === 0 ? (
        <DropzoneContainer
          {...getRootProps()}
          $isDragActive={isDragActive}
          $hasFiles={files.length > 0}
        >
          <input {...getInputProps()} ref={fileInputRef} />
          <DropzoneIcon $isDragActive={isDragActive}>
            📸
          </DropzoneIcon>
          <DropzoneTitle>
            {isDragActive ? 'Drop images here' : 'Upload Student Artwork'}
          </DropzoneTitle>
          <DropzoneText>
            Drag and drop image files here, or click to browse your computer.
            <br />
            Supports JPEG, PNG, GIF, WebP, HEIC, HEIF, DNG, TIFF files up to {formatFileSize(maxSize)} each.
            <br />
            Maximum {maxFiles} files per upload.
          </DropzoneText>
          <BrowseButton type="button">
            Choose Files
          </BrowseButton>
        </DropzoneContainer>
      ) : (
        <>
          <FilePreviewContainer>
            <h3>Selected Files ({files.length}/{maxFiles})</h3>
            <FilePreviewGrid>
              <AnimatePresence>
                {files.map((file, index) => {
                  const progress = uploadProgress.get(index);
                  return (
                    <FilePreviewCard
                      key={`${file.name}-${index}`}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      transition={{ duration: 0.3 }}
                    >
                      <PreviewImage
                        src={previews[index]}
                        alt={file.name}
                      />
                      <FileInfo>
                        <FileName>{file.name}</FileName>
                        <FileSize>{formatFileSize(file.size)}</FileSize>
                      </FileInfo>
                      
                      {!isUploading && (
                        <RemoveButton
                          onClick={() => removeFile(index)}
                          title="Remove file"
                        >
                          ✕
                        </RemoveButton>
                      )}

                      {progress && (
                        <>
                          <StatusIndicator $status={progress.status}>
                            {progress.status === 'uploading' || progress.status === 'processing' ? (
                              <LoadingSpinner size="sm" />
                            ) : (
                              getStatusIcon(progress.status)
                            )}
                          </StatusIndicator>
                          {progress.status === 'uploading' && (
                            <ProgressOverlay $progress={progress.progress} />
                          )}
                        </>
                      )}
                    </FilePreviewCard>
                  );
                })}
              </AnimatePresence>
            </FilePreviewGrid>
          </FilePreviewContainer>

          <UploadActions>
            <div>
              <ActionButton
                $variant="secondary"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading || files.length >= maxFiles}
              >
                Add More Files
              </ActionButton>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={acceptedFormats.join(',')}
                style={{ display: 'none' }}
                onChange={(e) => {
                  if (e.target.files) {
                    onDrop(Array.from(e.target.files), []);
                  }
                }}
              />
            </div>
            
            <div style={{ display: 'flex', gap: '16px' }}>
              <ActionButton
                $variant="danger"
                onClick={clearAllFiles}
                disabled={isUploading}
              >
                Clear All
              </ActionButton>
              <ActionButton
                onClick={handleUpload}
                disabled={isUploading || files.length === 0}
              >
                {isUploading ? (
                  <>
                    <LoadingSpinner size="sm" color="white" />
                    Uploading...
                  </>
                ) : (
                  `Upload ${files.length} ${files.length === 1 ? 'File' : 'Files'}`
                )}
              </ActionButton>
            </div>
          </UploadActions>
        </>
      )}

      {uploadResults && (
        <UploadSummary>
          <SummaryTitle>Upload Complete</SummaryTitle>
          <SummaryStats>
            <StatItem>
              <StatValue>{uploadResults.success}</StatValue>
              <StatLabel>Successful</StatLabel>
            </StatItem>
            <StatItem>
              <StatValue>{uploadResults.failed}</StatValue>
              <StatLabel>Failed</StatLabel>
            </StatItem>
            <StatItem>
              <StatValue>{files.length}</StatValue>
              <StatLabel>Total Files</StatLabel>
            </StatItem>
          </SummaryStats>
        </UploadSummary>
      )}

      {clearDialog}
    </UploadContainer>
  );
}